import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchAllFiles,
  fetchBitbucketFile,
  fetchBitbucketFilesInFolder,
  setCollapseLeftBar,
} from '../../features/organization/organizationSlice';
import { findBitbucketFileRecursively } from '../../utils/common';
import { useFileNavigationContext } from '../../contexts/FileNavigationContext';

/**
 * Custom hook that provides file operation handlers for the Leftbar
 */
const useFileOperationsHandler = (changeNavigationOnActiveFile) => {
  const dispatch = useDispatch();
  const { setCurrentSelected, setExpandedKeys, selectFile } = useFileNavigationContext();

  const activeWorkspace = useSelector((state) => state.organization?.activeWorkspace);
  const workspaceFoldersList = useSelector((state) => state.organization?.workspaceFoldersList);
  const folderMappings = useSelector((state) => state.organization?.folderMappings);
  const isAutoCollapseLeftbar = useSelector((state) => state.organization?.isAutoCollapseLeftbar);

  /**
   * Handle API file click
   */
  const openApiClickHandler = useCallback(
    (id, isApiFileParam = false) => {
      changeNavigationOnActiveFile({
        filePath: id,
        fileParent: null,
        isApiFile: isApiFileParam,
        workspaceId: activeWorkspace?.slug,
      });
    },
    [folderMappings, changeNavigationOnActiveFile, activeWorkspace, selectFile]
  );

  /**
   * Handle menu item click
   */
  const onMenuClickHandler = useCallback(
    async (id, { node, isOpen }) => {
      const splittedKeys = String(id)?.split('-');
      if (
        splittedKeys?.length > 0 &&
        splittedKeys[0] === 'tag' &&
        splittedKeys[splittedKeys.length - 1] === 'openApi'
      ) {
        return;
      }
      const isFolder = !node?.isFile && !node?.isApi;

      if (isFolder && id) {
        // dispatch(setCollapseLeftBar(true));
        // Toggle folder expansion using the context's expandedKeys
        setExpandedKeys((prev) => {
          if (prev.includes(id)) {
            return prev.filter((key) => key !== id);
          } else {
            return [...prev, id];
          }
        });

        const { meta_info } = node;
        if (!isOpen && activeWorkspace?.is_git_based) {
          const params = { scm_provider: activeWorkspace?.scm_provider, git_link: node?.git_link };
          dispatch(fetchBitbucketFilesInFolder(params));
        } else {
          if (!isOpen && splittedKeys?.length > 0 && splittedKeys[splittedKeys.length - 1] === 'openApiId')
            openApiClickHandler(id.split('-openApiId')[0]);
          if (!isOpen) dispatch(fetchAllFiles({ folder_id: id, workspace_id: activeWorkspace?.id }));
          if (meta_info && meta_info?.default_file)
            changeNavigationOnActiveFile({
              filePath: meta_info?.default_file_ref_id,
              fileParent: null,
              isApiFile: null,
              workspaceId: activeWorkspace?.slug,
            });
        }
      } else if (node?.isFile || node?.isApi) {
        if (isAutoCollapseLeftbar) {
          dispatch(setCollapseLeftBar(true));
        }
        if (node.isFile || node.isApi) {
          let filePath = node.ref_id;
          if (activeWorkspace?.is_git_based) {
            const _filePathArr = node.id?.split('/');
            const _fileName = _filePathArr[_filePathArr.length - 1]?.split('.')[0];
            changeNavigationOnActiveFile({
              filePath: _fileName,
              workspaceId: activeWorkspace?.slug,
            });
            const wsFile = findBitbucketFileRecursively(workspaceFoldersList, filePath);
            const params = {
              scm_provider: activeWorkspace?.scm_provider,
              git_link: wsFile?.git_link,
            };
            dispatch(fetchBitbucketFile({ params, file: wsFile }));
          } else {
            openApiClickHandler(node.ref_id);
          }
        }
      }
    },
    [
      activeWorkspace,
      openApiClickHandler,
      workspaceFoldersList,
      dispatch,
      setExpandedKeys,
      changeNavigationOnActiveFile,
      isAutoCollapseLeftbar,
    ]
  );

  return {
    openApiClickHandler,
    onMenuClickHandler,
  };
};

export default useFileOperationsHandler;
